import { Combobox, Transition } from '@headlessui/react';
import {
  CheckIcon,
  ChevronUpDownIcon,
  EnvelopeIcon,
  PhoneIcon,
  UserCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import Fuse from 'fuse.js';
import { UserProfile } from 'interfaces';
import dynamic from 'next/dynamic';
import {
  Fragment,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import classNames from 'utils/classnames';

const Avatar = dynamic(() => import('components/atoms/avatar'));

type ComplaintClient = UserProfile & {
  full_name?: string;
  given_name?: string;
  family_name?: string;
  contact_number?: string;
};

type ComplaintMember = {
  id: string;
  user?: {
    id?: string;
    name?: string;
    surname?: string;
    full_name?: string;
    given_name?: string;
    family_name?: string;
    email?: string;
    avatar?: Array<{ asset?: { url?: string } }>;
    user_assets?: Array<{ asset?: { url?: string } }>;
  };
};

type SearchableSelectProps<T extends { id: string }> = {
  items: T[];
  label: string;
  placeholder: string;
  searchPlaceholder: string;
  emptyMessage: string;
  selected: T | null;
  onChange: (value: T) => void;
  onClear: () => void;
  getDisplayValue: (value: T) => string;
  getSearchText: (value: T) => string;
  renderOption: (value: T) => ReactNode;
  renderSelectedSummary?: (value: T) => ReactNode;
};

const baseFuseOptions = {
  threshold: 0.28,
  minMatchCharLength: 2,
  ignoreLocation: true,
  keys: ['search'],
};

const compact = (value?: string | null) =>
  (value || '').replace(/\s+/g, ' ').trim();

const normalizeText = (value?: string | null) => compact(value).toLowerCase();

const getClientName = (client?: ComplaintClient | null) =>
  compact(
    client?.full_name ||
      `${client?.name || client?.given_name || ''} ${
        client?.surname || client?.family_name || ''
      }`,
  );

const getClientEmail = (client?: ComplaintClient | null) =>
  compact(client?.email);

const getClientPhone = (client?: ComplaintClient | null) =>
  compact(client?.phoneNumber || client?.contact_number);

const getMemberUser = (member?: ComplaintMember | null) => member?.user || null;

const getMemberName = (member?: ComplaintMember | null) => {
  const user = getMemberUser(member);

  return compact(
    user?.full_name ||
      `${user?.name || user?.given_name || ''} ${
        user?.surname || user?.family_name || ''
      }`,
  );
};

const getMemberEmail = (member?: ComplaintMember | null) => {
  const user = getMemberUser(member);
  return compact(user?.email);
};

const getMemberAvatar = (member?: ComplaintMember | null) => {
  const user = getMemberUser(member);

  return (
    user?.avatar?.[0]?.asset?.url || user?.user_assets?.[0]?.asset?.url || null
  );
};

function SearchableSelect<T extends { id: string }>({
  items,
  label,
  placeholder,
  searchPlaceholder,
  emptyMessage,
  selected,
  onChange,
  onClear,
  getDisplayValue,
  getSearchText,
  renderOption,
  renderSelectedSummary,
}: SearchableSelectProps<T>) {
  const [query, setQuery] = useState('');
  const fuse = useRef<Fuse<{ item: T; search: string }>>(null);

  const searchableItems = useMemo(
    () =>
      items.map((item) => ({
        item,
        search: getSearchText(item),
      })),
    [getSearchText, items],
  );

  useEffect(() => {
    fuse.current = new Fuse(searchableItems, baseFuseOptions);
  }, [searchableItems]);

  const results = useMemo(() => {
    if (!query.trim()) {
      return items.slice(0, 8);
    }

    if (!fuse.current) {
      return [];
    }

    return fuse.current
      .search(normalizeText(query))
      .map((result) => result.item.item)
      .slice(0, 8);
  }, [items, query]);

  return (
    <Combobox
      value={selected}
      onChange={(value: T) => {
        onChange(value);
        setQuery('');
      }}
    >
      {({ open }) => (
        <div className="relative border-t border-slate-100 pt-4">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm font-semibold text-slate-900">
              {label}
            </span>
            {selected ? (
              <button
                type="button"
                onClick={() => {
                  setQuery('');
                  onClear();
                }}
                className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2 py-1 text-[11px] font-medium text-slate-500 transition hover:border-slate-300 hover:text-slate-700"
              >
                <XMarkIcon className="h-3.5 w-3.5" />
                Clear
              </button>
            ) : null}
          </div>

          <div className="relative">
            <Combobox.Input
              autoComplete="off"
              displayValue={(value: T) => (value ? getDisplayValue(value) : '')}
              onChange={(event) => setQuery(event.target.value)}
              onFocus={(event) => {
                if (open) {
                  return;
                }

                const button = event.currentTarget
                  .nextElementSibling as HTMLButtonElement;
                button?.click();
              }}
              placeholder={selected ? searchPlaceholder : placeholder}
              className="w-full rounded-xl border border-slate-200 bg-white py-3 pl-4 pr-11 text-sm text-slate-900 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
            />
            <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400">
              <ChevronUpDownIcon className="h-5 w-5" aria-hidden="true" />
            </Combobox.Button>
          </div>

          {selected && renderSelectedSummary ? (
            <div className="mt-3 rounded-2xl border border-slate-200 bg-slate-50/80 p-3">
              {renderSelectedSummary(selected)}
            </div>
          ) : null}

          <Transition
            as={Fragment}
            show={open}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Combobox.Options className="absolute z-[60] mt-2 max-h-80 w-full overflow-auto rounded-2xl border border-slate-200 bg-white p-2 shadow-2xl ring-1 ring-black/5 focus:outline-none">
              {results.length === 0 ? (
                <div className="px-3 py-4 text-sm text-slate-500">
                  {emptyMessage}
                </div>
              ) : (
                results.map((item) => (
                  <Combobox.Option
                    key={item.id}
                    value={item}
                    className={({ active }) =>
                      classNames(
                        'relative cursor-pointer rounded-xl px-3 py-3 transition',
                        active ? 'bg-teal-50 text-slate-900' : 'text-slate-700',
                      )
                    }
                  >
                    {({ selected: optionSelected, active }) => (
                      <div className="flex items-start gap-3">
                        <span
                          className={classNames(
                            'mt-1 inline-flex h-5 w-5 flex-none items-center justify-center rounded-full border',
                            optionSelected
                              ? 'border-teal-600 bg-teal-600 text-white'
                              : active
                                ? 'border-teal-200 text-teal-500'
                                : 'border-slate-200 text-transparent',
                          )}
                        >
                          <CheckIcon className="h-3.5 w-3.5" />
                        </span>
                        <div className="min-w-0 flex-1">
                          {renderOption(item)}
                        </div>
                      </div>
                    )}
                  </Combobox.Option>
                ))
              )}
            </Combobox.Options>
          </Transition>
        </div>
      )}
    </Combobox>
  );
}

export function ComplaintClientSearch({
  clients,
  selected,
  onChange,
  onClear,
}: {
  clients: ComplaintClient[];
  selected: ComplaintClient | null;
  onChange: (client: ComplaintClient) => void;
  onClear: () => void;
}) {
  return (
    <SearchableSelect
      items={clients || []}
      label="Find customer"
      placeholder="Search by name, email or phone"
      searchPlaceholder="Search for a different customer"
      emptyMessage="No customer matches your search."
      selected={selected}
      onChange={onChange}
      onClear={onClear}
      getDisplayValue={(value) => getClientName(value)}
      getSearchText={(value) =>
        normalizeText(
          `${getClientName(value)} ${getClientEmail(value)} ${getClientPhone(
            value,
          )}`,
        )
      }
      renderOption={(client) => (
        <div className="space-y-1">
          <p className="truncate text-sm font-semibold text-slate-900">
            {getClientName(client)}
          </p>
          <div className="space-y-0.5 text-xs text-slate-500">
            {getClientEmail(client) ? (
              <p className="flex items-center gap-1.5 lowercase">
                <EnvelopeIcon className="h-3.5 w-3.5" />
                {getClientEmail(client)}
              </p>
            ) : null}
            {getClientPhone(client) ? (
              <p className="flex items-center gap-1.5">
                <PhoneIcon className="h-3.5 w-3.5" />
                {getClientPhone(client)}
              </p>
            ) : null}
          </div>
        </div>
      )}
      renderSelectedSummary={(client) => (
        <div className="space-y-1">
          <p className="text-sm font-semibold text-slate-900">
            {getClientName(client)}
          </p>
          <div className="space-y-0.5 text-xs text-slate-500">
            {getClientEmail(client) ? (
              <p className="flex items-center gap-1.5 lowercase">
                <EnvelopeIcon className="h-3.5 w-3.5" />
                {getClientEmail(client)}
              </p>
            ) : null}
            {getClientPhone(client) ? (
              <p className="flex items-center gap-1.5">
                <PhoneIcon className="h-3.5 w-3.5" />
                {getClientPhone(client)}
              </p>
            ) : null}
          </div>
        </div>
      )}
    />
  );
}

export function ComplaintMemberSearch({
  members,
  selected,
  onChange,
  onClear,
}: {
  members: ComplaintMember[];
  selected: ComplaintMember | null;
  onChange: (member: ComplaintMember) => void;
  onClear: () => void;
}) {
  return (
    <SearchableSelect
      items={members || []}
      label="Assign to"
      placeholder="Search by team member name or email"
      searchPlaceholder="Search for a different assignee"
      emptyMessage="No team member matches your search."
      selected={selected}
      onChange={onChange}
      onClear={onClear}
      getDisplayValue={(value) => getMemberName(value)}
      getSearchText={(value) =>
        normalizeText(`${getMemberName(value)} ${getMemberEmail(value)}`)
      }
      renderOption={(member) => {
        const avatar = getMemberAvatar(member);

        return (
          <div className="flex items-center gap-3">
            {avatar ? (
              <Avatar
                size={{ width: 10, height: 10 }}
                layout="fill"
                src={avatar}
                alt={getMemberName(member)}
              />
            ) : (
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-400">
                <UserCircleIcon className="h-6 w-6" />
              </span>
            )}
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-slate-900">
                {getMemberName(member)}
              </p>
              {getMemberEmail(member) ? (
                <p className="truncate text-xs text-slate-500 lowercase">
                  {getMemberEmail(member)}
                </p>
              ) : null}
            </div>
          </div>
        );
      }}
      renderSelectedSummary={(member) => {
        const avatar = getMemberAvatar(member);

        return (
          <div className="flex items-center gap-3">
            {avatar ? (
              <Avatar
                size={{ width: 10, height: 10 }}
                layout="fill"
                src={avatar}
                alt={getMemberName(member)}
              />
            ) : (
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-400">
                <UserCircleIcon className="h-6 w-6" />
              </span>
            )}
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-slate-900">
                {getMemberName(member)}
              </p>
              {getMemberEmail(member) ? (
                <p className="truncate text-xs text-slate-500 lowercase">
                  {getMemberEmail(member)}
                </p>
              ) : null}
            </div>
          </div>
        );
      }}
    />
  );
}
